// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The validation overlay, as the bench draws it (M13-17, 11 §3.4).
//
// The 2-D Builder shades the reachability map under the parts, lists the geometry classes'
// complaints down the side, and selects the part a complaint is about when it is clicked. The
// bench has the same report (`reportFor` → `analyseWithReachability`, the one pass that gives
// the complaints and the grid) and no side panel, so what it draws is the report *on the
// machine*: the map as a decal on the surface — the open space a ball cannot reach tinted, a
// trap filled amber with its edge lit —, a callout hanging over every part a complaint names (and
// over the largest trapped pocket for a trap, which is about a place and not a part), and for a
// flipper pair whose resting gap is at fault, the measurement itself: a line between the two tips
// with the gap in millimetres, because "the centre gap is 21.3 mm" is a sentence and a line on
// the bench is a fact the author can see against the ball.
//
// **Tapping a callout selects the part** — the Builder's click on a complaint, as a fingertip
// through the callout's face (`calloutTaps.ts`): the halo lights on the part and the complaint's
// own sentence is said, so an author can find what a callout is about without reading it.
//
// **Pure.** A definition and its report in; what to draw out, in playfield metres. Nothing here
// knows Three or Unity; the native `ValidationView.cs` is this file, case for case, and the decal
// pixels are the same bytes on both clients (`decalPixels`).

import { devicePosition, type MachineDefinition, type Point2, type Point3 } from '../../../core/ts/machine'
import { flipperPairs, type GeometryIssue, type ReachabilityMap } from '../../../core/ts/machine/geometry'
import { deviceIndexOf, layerIndexOf, shortLabel } from '../../builder/validation'
import { isSplineLayer } from '../../builder/splines'
import { apexOf } from './rampPoints'
import { BENCH_EN, benchText, type BenchKey, type BenchStrings } from './benchStrings'

export type Severity = 'error' | 'warning'

/** A complaint, hung over the place it is about. */
export interface Callout {
  readonly code: string
  /** The Builder's short label: *Ball trap*, *Unreachable*, *Flippers touch*. */
  readonly label: string
  /** The whole sentence, said when the callout is tapped. */
  readonly message: string
  readonly severity: Severity
  /** The part it is about, or nothing for a complaint about a place. */
  readonly deviceId: string | undefined
  /** The ramp or habitrail it is about (M13-24), or nothing. */
  readonly layerId: string | undefined
  readonly at: Point2
  /** How far over the surface the place is, playfield metres: a ramp's apex is up in the air, a part is on the wood. */
  readonly lift: number
}

/** A flipper pair's resting gap, drawn between the tips. */
export interface Measurement {
  readonly leftId: string
  readonly rightId: string
  /** The left bat's resting tip, and the right's. */
  readonly from: Point2
  readonly to: Point2
  /** The clear space between the tips, metres. Negative means they overlap. */
  readonly gap: number
  /** *21.3 mm*, or *overlap 3.2 mm*. */
  readonly text: string
  readonly severity: Severity
}

export interface ValidationView {
  /** The reachability decal, or nothing when the fill could not run. */
  readonly map: ReachabilityMap | undefined
  readonly callouts: readonly Callout[]
  readonly measures: readonly Measurement[]
  readonly errors: number
  readonly warnings: number
  /** Changes when the picture should be rebuilt: the complaints, their places, the gaps. */
  readonly key: string
}

/** The key a callout is drawn and tapped under: what and where. */
export function calloutKey(callout: Callout): string {
  return `${callout.code}@${callout.deviceId ?? callout.layerId ?? 'place'}`
}

/** The report, as `reportFor` answers it. */
export interface ReportLike {
  readonly issues: readonly GeometryIssue[]
  readonly map: ReachabilityMap | undefined
}

/** How many callouts the bench hangs: past this, the machine is a list, not a picture. */
export const MAX_CALLOUTS = 12

/**
 * The largest trapped pocket on the map, by its centre and its size in cells — where the trap
 * callout hangs. The geometry pass reports the same pocket in its sentence; this finds it again
 * from the map rather than parsing the sentence, so a change in the wording cannot move the mark.
 */
export function largestPocket(map: ReachabilityMap): { readonly at: Point2; readonly cells: number } | undefined {
  const seen = new Uint8Array(map.trapped.length)
  let best: { at: Point2; cells: number } | undefined
  for (let start = 0; start < map.trapped.length; start++) {
    if (map.trapped[start] === 0 || seen[start] === 1) continue
    const queue = [start]
    seen[start] = 1
    let head = 0
    let sumX = 0
    let sumY = 0
    while (head < queue.length) {
      const at = queue[head++] as number
      const col = at % map.cols
      const row = (at - col) / map.cols
      sumX += col
      sumY += row
      for (const [dc, dr] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ] as const) {
        const c = col + dc
        const r = row + dr
        if (c < 0 || r < 0 || c >= map.cols || r >= map.rows) continue
        const next = r * map.cols + c
        if (map.trapped[next] === 0 || seen[next] === 1) continue
        seen[next] = 1
        queue.push(next)
      }
    }
    const cells = queue.length
    if (best === undefined || cells > best.cells) {
      best = {
        at: [map.minX + (sumX / cells + 0.5) * map.cell, map.minY + (sumY / cells + 0.5) * map.cell],
        cells,
      }
    }
  }
  return best
}

/** The decal's pixels: RGBA, `cols × rows`, row 0 at `minY` (the near edge), as a texture takes them. */
export interface DecalPixels {
  readonly width: number
  readonly height: number
  readonly rgba: Uint8Array
}

/** The tint on open space a ball cannot reach: the Builder's `unreachable`, red at 16 %. */
export const UNREACHABLE_RGBA: readonly [number, number, number, number] = [255, 74, 96, 41]
/** A trap's fill: the Builder's `trap`, amber at 50 %. */
export const TRAP_RGBA: readonly [number, number, number, number] = [255, 176, 32, 128]
/** A trap's edge, lit: the same amber, near solid, so the pocket reads as outlined. */
export const TRAP_EDGE_RGBA: readonly [number, number, number, number] = [255, 176, 32, 242]

/**
 * The map as pixels.
 *
 * **Blocked is not drawn** (the Builder's rule): shading where the ball cannot go would trace the
 * outline of everything already drawn. What shows is open space the ball cannot get to, and the
 * traps — filled, and edged where a trapped cell meets one that is not, so a pocket has an
 * outline rather than a smear.
 */
export function decalPixels(map: ReachabilityMap): DecalPixels {
  const rgba = new Uint8Array(map.cols * map.rows * 4)
  for (let row = 0; row < map.rows; row++) {
    for (let col = 0; col < map.cols; col++) {
      const i = row * map.cols + col
      if (map.blocked[i] !== 0) continue
      let colour: readonly [number, number, number, number] | undefined
      if (map.trapped[i] === 1) {
        colour = onEdge(map, col, row) ? TRAP_EDGE_RGBA : TRAP_RGBA
      } else if (map.reachable[i] === 0) {
        colour = UNREACHABLE_RGBA
      }
      if (colour === undefined) continue
      rgba[i * 4] = colour[0]
      rgba[i * 4 + 1] = colour[1]
      rgba[i * 4 + 2] = colour[2]
      rgba[i * 4 + 3] = colour[3]
    }
  }
  return { width: map.cols, height: map.rows, rgba }
}

function onEdge(map: ReachabilityMap, col: number, row: number): boolean {
  for (const [dc, dr] of [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as const) {
    const c = col + dc
    const r = row + dr
    if (c < 0 || r < 0 || c >= map.cols || r >= map.rows) return true
    if (map.trapped[r * map.cols + c] === 0) return true
  }
  return false
}

function tipOf(definition: MachineDefinition, index: number): Point2 | undefined {
  const device = definition.devices[index]
  if (device === undefined || device.kind !== 'flipper') return undefined
  return [
    device.pivot[0] + device.length * Math.cos(device.restAngle),
    device.pivot[1] + device.length * Math.sin(device.restAngle),
  ]
}

function gapText(gap: number, strings: BenchStrings): string {
  return gap <= 0
    ? benchText(strings, 'measure.overlap', { mm: (-gap * 1000).toFixed(1) })
    : benchText(strings, 'measure.gap', { mm: (gap * 1000).toFixed(1) })
}

/**
 * Where a complaint about a ramp hangs (M13-24): over its exit — the last control point — for a
 * complaint about where it leads, over its apex for everything else, since the apex is where a
 * ramp is too tall or too steep. Nothing for a spline with no points.
 */
function rampPlace(spline: readonly Point3[], code: string): { readonly at: Point2; readonly lift: number } | undefined {
  if (code.startsWith('layer-exit')) {
    const last = spline[spline.length - 1]
    return last === undefined ? undefined : { at: [last[0], last[1]], lift: Math.max(0, last[2]) }
  }
  return apexOf(spline)
}

/** The callout's short label in the bench's language, or the Builder's English for a code the table has not met. */
function calloutLabel(issue: GeometryIssue, strings: BenchStrings): string {
  const key = `callout.${issue.code}` as BenchKey
  const known: string | undefined = (strings as Readonly<Record<string, string>>)[key]
  return known ?? shortLabel(issue)
}

/**
 * What the bench draws for this report.
 *
 * A complaint about a part hangs over the part (`devicePosition`); a trap hangs over its largest
 * pocket; a complaint about a ramp or a habitrail hangs over the ramp (M13-24) — at its apex,
 * up in the air where the ramp is, or at its exit for a complaint about where it leads; a
 * complaint with no place on the playfield — a plane, the machine as a whole — has no callout,
 * and the count still carries it. A flipper pair at fault gets its measurement.
 */
export function validationViewOf(definition: MachineDefinition, report: ReportLike, strings: BenchStrings = BENCH_EN): ValidationView {
  const callouts: Callout[] = []
  const measures: Measurement[] = []
  const pocket = report.map === undefined ? undefined : largestPocket(report.map)
  const pairs = flipperPairs(definition)
  let errors = 0
  let warnings = 0

  for (const issue of report.issues) {
    if (issue.severity === 'error') errors++
    else warnings++
    const index = deviceIndexOf(issue)
    const device = index === undefined ? undefined : definition.devices[index]

    if ((issue.code === 'flippers-interpenetrate' || issue.code === 'no-centre-drain') && index !== undefined) {
      const pair = pairs.find((p) => p.leftIndex === index)
      const from = pair === undefined ? undefined : tipOf(definition, pair.leftIndex)
      const to = pair === undefined ? undefined : tipOf(definition, pair.rightIndex)
      if (pair !== undefined && from !== undefined && to !== undefined) {
        measures.push({
          leftId: pair.leftId,
          rightId: pair.rightId,
          from,
          to,
          gap: pair.gap,
          text: gapText(pair.gap, strings),
          severity: issue.severity,
        })
      }
    }

    if (callouts.length >= MAX_CALLOUTS) continue
    const layerIndex = layerIndexOf(issue)
    const layer = layerIndex === undefined ? undefined : definition.layers[layerIndex]
    if (device !== undefined) {
      const at = devicePosition(device)
      if (at === undefined) continue
      callouts.push({ code: issue.code, label: calloutLabel(issue, strings), message: issue.message, severity: issue.severity, deviceId: device.id, layerId: undefined, at, lift: 0 })
    } else if (layer !== undefined && isSplineLayer(layer)) {
      const place = rampPlace(layer.spline, issue.code)
      if (place === undefined) continue
      callouts.push({ code: issue.code, label: calloutLabel(issue, strings), message: issue.message, severity: issue.severity, deviceId: undefined, layerId: layer.id, at: place.at, lift: place.lift })
    } else if (issue.code === 'ball-trap' && pocket !== undefined) {
      callouts.push({ code: issue.code, label: calloutLabel(issue, strings), message: issue.message, severity: issue.severity, deviceId: undefined, layerId: undefined, at: pocket.at, lift: 0 })
    }
  }

  // The key names every callout by what and where, every measurement by its gap, and the
  // counts — so a part that moves under the same complaint moves its callout too.
  const key = [
    ...callouts.map((c) => `${calloutKey(c)}@${c.at[0].toFixed(4)},${c.at[1].toFixed(4)},${c.lift.toFixed(4)}`),
    ...measures.map((m) => `gap:${m.leftId}:${m.rightId}:${m.gap.toFixed(5)}`),
    `${errors}/${warnings}`,
    report.map === undefined ? 'nomap' : `map:${report.map.cols}x${report.map.rows}`,
  ].join('|')
  return { map: report.map, callouts, measures, errors, warnings, key }
}
